'use client';

import type { AuthState } from '../../types';
import type { IOrganisation } from 'src/types/nya/common';

import React, { useCallback, useEffect, useMemo } from 'react';
import { onIdTokenChanged } from 'firebase/auth'; // onIdTokenChanged also handles updates to the token (like emailVerified status change)
import { useSetState } from 'minimal-shared/hooks';
import { usePathname } from 'src/routes/hooks';
import axios from 'src/lib/axios';
import { AuthContext } from '../auth-context';
import { firebaseClientAuth } from '@/src/lib/firebase/clientApp';
import { HämtaOrganisationerFörAnvändare } from "src/sections/organisation/OrganisationsService";
import { hämtaAnvändare } from "src/sections/anvandare/AnvandarService";
import { CustomErrorView } from "src/components/custom-error/custom-error-view";

type Props = {
    children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
    const pathname = usePathname();

    const { state, setState } = useSetState<AuthState>({
        anvandare: undefined,
        organisationer: [],
        user: null,
        loading: true, // Default to true, but we will ensure it resolves
        error: null,
    });


    useEffect(() => {
        const unsubscribe = onIdTokenChanged(firebaseClientAuth, async (user: AuthState["user"]) => {
            try {
                if (user) {
                    const token = await (user as any).getIdToken();
                    localStorage.setItem('accessToken', token);
                    axios.defaults.headers.common.Authorization = `Bearer ${token}`;

                    // If we're on auth/onboarding pages, skip user fetch to avoid 401/404 errors during account creation.
                    // /presentation är publik marknadsföring och ligger utanför (dashboard). Den som demar är
                    // inloggad, och utan undantaget här kastar onboarding-redirecten nedan ut hen från sidan.
                    // Undantaget räcker på ett ställe: det ger en tidig return före både användarhämtningen
                    // och de två window.location-hoppen.
                    const isAuthOrOnboardingPage = ['/registrera', '/verifiera', '/auth/action', '/aterstall', '/logga-in', '/presentation'].some(path => pathname.startsWith(path));

                    const emailVerified = user.emailVerified;
                    if (isAuthOrOnboardingPage || !emailVerified) {
                        setState({ user: { ...user }, loading: false, error: null });
                        return;
                    }

                    setState({ loading: true, error: null }); 

                    let anvandare = undefined;
                    let organisationer: IOrganisation[] = [];
                    try {
                        anvandare = await hämtaAnvändare();
                    } catch (e: any) {
                        if (e.response && (e.response.status === 404 || e.response.status === 401)) {
                            // No user in DB — redirect to registration (guard against loop)
                            
                            // Sign out of Firebase Auth to break the redirect loop
                            try {
                                await firebaseClientAuth.signOut();
                            } catch (signOutErr) {
                                console.error("Error signing out of Firebase:", signOutErr);
                            }

                            localStorage.removeItem('accessToken');
                            delete axios.defaults.headers.common.Authorization;
                            setState({ user: null, anvandare: undefined, loading: false, error: null });
                            if (typeof window !== 'undefined' && !pathname.startsWith('/registrera')) {
                                window.location.href = '/registrera';
                            }
                            return;
                        }
                        console.error("Kunde inte hämta anvandare. Fel: ", e);
                        setState({ user: null, anvandare: undefined, loading: false, error: "Just nu har vi tekniska problem. Var vänlig försök igen senare" });
                        return;
                    }
                    try {
                        organisationer = await HämtaOrganisationerFörAnvändare();
                    } catch (_) {
                        organisationer = [];
                    }
                    setState({ user: { ...user }, anvandare, organisationer, loading: false, error: null });

                    // Onboarding-omdirigeringen låg här förut, men bara här — den lokala
                    // providern och dev-personas saknade den och hoppade därför över
                    // onboardingen helt. Regeln bor numera i AuthGuard, som alla providrar
                    // delar.

                } else {
                    localStorage.removeItem('accessToken');
                    delete axios.defaults.headers.common.Authorization;
                    setState({ user: null, anvandare: undefined, loading: false, error: null });
                }
            } catch (error) {
                console.error("[AuthProvider] Auth State Change Logic Error:", error);
                setState({ user: null, anvandare: undefined, loading: false, error: "Ett oväntat fel inträffade vid inloggning." });
            }
        });

        return () => unsubscribe();
    }, [setState, pathname]); // pathname included so it re-runs when navigation happens if we skipped it previously

    const checkUserSession = useCallback(async () => {
        // This function can be used to manually force a token refresh if needed,
        // but typically firebase handles this.
        // For the 55 min refresh requirement (refreshing custom signed URLs etc), 
        // we can just force a token refresh.
        const user = firebaseClientAuth.currentUser;
        if (user) {
            const newToken = await user.getIdToken(true);
            localStorage.setItem('accessToken', newToken);
            axios.defaults.headers.common.Authorization = `Bearer ${newToken}`;
            try {
                const anvandare = await hämtaAnvändare();
                let organisationer: IOrganisation[] = [];
                try {
                    organisationer = await HämtaOrganisationerFörAnvändare();
                } catch (_) {
                    organisationer = [];
                }
                setState({ anvandare, organisationer });
            } catch (e) {
                console.error("Could not fetch anvandare in checkUserSession", e);
            }
        }
    }, [setState]);

    useEffect(() => {
        // Refresh use session (token) every 55 minutes
        const interval = setInterval(() => {
            checkUserSession();
        }, 55 * 60 * 1000);

        return () => clearInterval(interval);
    }, [checkUserSession]);

    // ----------------------------------------------------------------------

    const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

    const status = state.loading ? 'loading' : checkAuthenticated;

    const memoizedValue = useMemo(
        () => ({
            anvandare: state.anvandare,
            organisationer: state.organisationer,
            user: state.user
                ? {
                    ...state.user,
                    id: state.user?.uid,
                    accessToken: state.user?.accessToken,
                    displayName: state.user?.displayName,
                    photoURL: state.user?.photoURL,
                    role: state.user?.role ?? 'admin',
                }
                : null,
            checkUserSession,
            loading: status === 'loading',
            error: state.error,
            authenticated: status === 'authenticated',
            unauthenticated: status === 'unauthenticated',
        }),
        [checkUserSession, state.user, status, state.organisationer, state.anvandare, state.error]
    );

    if (state.error) {
        return (
            <AuthContext.Provider value={memoizedValue}>
                <CustomErrorView
                    error={{ name: 'Auth Error', message: state.error } as Error}
                    title="Inloggning misslyckades"
                    message={state.error}
                />
            </AuthContext.Provider>
        );
    }

    return <AuthContext value={memoizedValue}>{children}</AuthContext>;
}
